import React, { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useLocation } from "react-router-dom";

import Svg from "../../../components/svg/Svg";
import PageLayout from "../../_layout/PageLayout";
import { Warning } from "../../../components/svg/svgImageList";
import { currentUserService } from "../../../services/user/CurrentUserStorage";
import { SubscriptionMenuItemsModel } from "../SubscriptionMenuItems";
import { ManageSubscriptions } from "../../../services/authorization/Permissions";
import {
  OrganizationSubscriptionPlansStorageModel,
  SubscriptionPlanSelection,
} from "../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import { api } from "../../../services/api/ApiService";
import { SubscriptionPlan } from "../../../services/organization-subscription-plans/SubscriptionPlan";
import { SubscriptionCard } from "./SubscriptionCard";
import { translationService } from "../../../services/translation/TranslationService";
import "./composition.scss";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import { appSessionStorage } from "../../../services/storage/SessionStorageService";
import { EnterpriseSubscriptionCard } from "./components/enterprise-card/EnterpriseSubscriptionCard";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import { TermsAndConditions } from "../../../components/terms-and-conditions/TermsAndConditions";

export interface CardsDirtyState {
  [subscriptionPlanId: number]: boolean;
}

export default observer(function PlansPage() {
  const location = useLocation();

  const [subscriptionMenuItems] = useState(() => SubscriptionMenuItemsModel.create({}, {}));

  const menuItems = {
    staticItems: subscriptionMenuItems.getStaticMenuItems(),
  };

  const [organizationSubscriptionPlansStorage] = useState(() =>
    OrganizationSubscriptionPlansStorageModel.create({}, { api })
  );

  const [cardsDirtyState, setCardsDirtyState] = useState<CardsDirtyState>({});

  const getSubscriptionPlans = useCallback(() => {
    return organizationSubscriptionPlansStorage.subscriptionPlans.map((plan: SubscriptionPlan) => {
      return (
        <SubscriptionCard
          key={plan.id}
          subscriptionPlan={plan}
          organizationSubscriptionPlansStorage={organizationSubscriptionPlansStorage}
          setCardsDirtyState={setCardsDirtyState}
        />
      );
    });
  }, [organizationSubscriptionPlansStorage]);

  useEffect(() => {
    const checkoutSessionId = new URLSearchParams(location.search).get("checkoutSessionId");
    if (checkoutSessionId && organizationSubscriptionPlansStorage.isActiveSubscriptionPaid) {
      clearPreviousSelectionFromSessionStorage();
      snackbarNotificationsService.showSuccess("SubscriptionPlans.successfully-subscribed", {
        nameOfPlan: translationService.translate(`SubscriptionPlans.card.${organizationSubscriptionPlansStorage.currentActiveSubscription.name}`),
      });
    }
  }, [location.search, organizationSubscriptionPlansStorage.currentActiveSubscription.name, organizationSubscriptionPlansStorage.isActiveSubscriptionPaid]);

  useEffect(() => {
    if (!organizationSubscriptionPlansStorage.isLoading) {
      if (!organizationSubscriptionPlansStorage.isActiveSubscriptionPaid) {
        const subscriptionPlanSelection: SubscriptionPlanSelection = JSON.parse(
          appSessionStorage.getStorageItem("SubscriptionPlanSelection")
        );

        if (subscriptionPlanSelection) {
          organizationSubscriptionPlansStorage.updateSubscriptionPlanWithPreviousSelection(
            subscriptionPlanSelection
          );

          clearPreviousSelectionFromSessionStorage();
        }
      }
    }
  }, [organizationSubscriptionPlansStorage.isActiveSubscriptionPaid, organizationSubscriptionPlansStorage.isLoading, organizationSubscriptionPlansStorage]);

  const clearPreviousSelectionFromSessionStorage = () => {
    appSessionStorage.removeStorageItem("SubscriptionPlanSelection");
  };

  useEffect(() => {
    warningLeavePageService.setIsDirty(
      Object.values(cardsDirtyState).some((isCardDirty: boolean) => isCardDirty)
    );
  }, [cardsDirtyState]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ManageSubscriptions]}
      id="view-subscription-plans"
      titleKey="SubscriptionPlans.title"
      isLoading={
        organizationSubscriptionPlansStorage.isLoading || warningLeavePageService.isSubmitting
      }
      menuItems={menuItems}
    >
      <div className="container">
        {currentUserService.hasCurrentOrganizationVatWarning && (
          <div className="top-warning">
            <Svg className="icon-warning" svg={Warning} width={25} height={25} />
            <span>
              {translationService.translate(
                "SubscriptionPlans.warning.missingOrganizationVatNumber.beforeUrl"
              )}
              <NavLink to="/settings/organization-details">
                {translationService.translate(
                  "SubscriptionPlans.warning.missingOrganizationVatNumber.urlText"
                )}
              </NavLink>
              {translationService.translate(
                "SubscriptionPlans.warning.missingOrganizationVatNumber.afterUrl"
              )}
            </span>
          </div>
        )}

        <div className="subscription-plans">
          {getSubscriptionPlans()}
          {!organizationSubscriptionPlansStorage.containsEnterprisePlan
            && <EnterpriseSubscriptionCard />}
        </div>
        <TermsAndConditions
          textKeyBeforeLinks="SubscriptionPlans.terms-and-conditions-acceptance-part-1"
          textKeyAfterLinks="SubscriptionPlans.terms-and-conditions-acceptance-part-2"
        />
      </div>
    </PageLayout>
  );
});
